import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { getDigitainUrl } from "@actions/thirdParty";
import { wireBettoDigitainBetConfirmedAlert } from "@layouts/betto/hooks/wireBettoDigitainBetConfirmedAlert";
import { BettoLoadingLogo } from "@layouts/betto/shared/BettoLoadingLogo";
import {
	BettoGameStage,
	BettoGameStageSkeleton,
} from "@pages/casino/pages/gameplay/betto/bettoGameplayElements.js";
import Bootstrapper from "./assets/js/bootstrapper";
import "./assets/js/digitain";

const SportsbookRoot = styled.div`
	position: relative;
	width: 100%;
	min-height: min(56vh, calc(100vh - var(--bt-header-height, 62px) - 120px));
`;

const SportsbookFrame = styled.div`
	width: 100%;
	height: 100%;
	min-height: inherit;
`;

const SportsbookLoadingOverlay = styled.div`
	position: absolute;
	inset: 0;
	z-index: 2;
`;

const waitForSportsbookMount = (container) =>
	new Promise((resolve) => {
		const hasContent = () =>
			Boolean(
				container.querySelector("sport-mobile") ||
					container.querySelector("iframe") ||
					container.childElementCount > 0
			);

		if (hasContent()) {
			resolve();
			return;
		}

		const observer = new MutationObserver(() => {
			if (!hasContent()) return;
			observer.disconnect();
			clearTimeout(timeoutId);
			resolve();
		});

		observer.observe(container, { childList: true, subtree: true });

		const timeoutId = setTimeout(() => {
			observer.disconnect();
			resolve();
		}, 8000);
	});

export default function Digitain({ sessionId, currentLanguage, showAlert }) {
	const isMobileRef = useRef(null);
	const containerRef = useRef(null);
	const isLatinoUI = window.DIGITAIN_LATINO_UI === true;
	const betConfirmedCleanupRef = useRef(null);
	const isBetto = window.TENANT === "betto";
	const [isBootstrapping, setIsBootstrapping] = useState(isBetto);

	useEffect(() => {
		if (!isBetto || typeof showAlert !== "function") {
			return undefined;
		}

		betConfirmedCleanupRef.current?.();
		betConfirmedCleanupRef.current = wireBettoDigitainBetConfirmedAlert(showAlert);

		return () => {
			betConfirmedCleanupRef.current?.();
			betConfirmedCleanupRef.current = null;
		};
	}, [isBetto, showAlert]);

	useEffect(() => {
		let lastPath = window.location.pathname;
		const removeSlip = () => {
			if (window.location.pathname !== "/sportsbook") {
				const el = document.querySelector("sport-betslip");
				if (el) el.remove();
			}
		};
		const checkPath = () => {
			if (window.location.pathname !== lastPath) {
				lastPath = window.location.pathname;
				removeSlip();
			}
		};
		const origPush = window.history.pushState;
		window.history.pushState = function () {
			origPush.apply(this, arguments);
			checkPath();
		};

		window.addEventListener("popstate", checkPath);
		removeSlip();
		return () => {
			window.history.pushState = origPush;
			window.removeEventListener("popstate", checkPath);
		};
	}, []);

	const renderSB = useCallback(async () => {
		const isMobile = window.innerWidth < 1024;

		if (isMobileRef.current === isMobile) return;

		isMobileRef.current = isMobile;

		const searchParams = new URLSearchParams(window.location.search);
		const returnUrl = searchParams.get("returnUrl");
		const isLive = returnUrl === "live";

		async function getToken() {
			try {
				const response = await getDigitainUrl();
				return response?.data?.token ?? "-";
			} catch (error) {
				console.error("Request failed:", error.message);
				return "-";
			}
		}

		const container = containerRef.current;
		if (!container) {
			if (isBetto) {
				setIsBootstrapping(false);
			}
			return;
		}

		if (isBetto) {
			setIsBootstrapping(true);
		}

		try {
			container.innerHTML = "";
			if (isMobile) {
				await Bootstrapper.boot(
					{
						server: window.DIGITAIN_HOST,
						token: sessionId && sessionId !== "" ? await getToken() : "-",
						containerId: "sport_div_iframe",
						oddsFormat: window.DIGITAIN_ODD_FORMAT,
						defaultLanguage: currentLanguage || "en",
						sportPartner: window.DIGITAIN_TOKEN,
						userId: sessionId && sessionId !== "" ? sessionId : "",
						isLive: isLive,
						...(isBetto && {
							initialRoute: {
								path: isLive ? "/live" : "/pre-match",
								qs: {},
								hashData: "",
							},
						}),
					},
					{
						name: "Mobile",
					}
				);
			} else if (isLatinoUI) {
				document.body.classList.add("latino-ui");
				await Bootstrapper.boot(
					{
						server: window.DIGITAIN_HOST,
						token: sessionId && sessionId !== "" ? await getToken() : "-",
						containerId: "sport_div_iframe",
						oddsFormat: window.DIGITAIN_ODD_FORMAT,
						defaultLanguage: currentLanguage || "en",
						sportPartner: window.DIGITAIN_TOKEN,
						userId: sessionId && sessionId !== "" ? sessionId : "",
						isLive: isLive,
						...(isBetto && {
							initialRoute: {
								path: isLive ? "/live" : "/pre-match",
								qs: {},
								hashData: "",
							},
						}),
					},
					{
						name: "LatinoView",
					}
				);
			} else {
				const sp = [
					["server", window.DIGITAIN_HOST],
					["token", sessionId && sessionId !== "" ? await getToken() : "-"],
					["currentPage", "Home"],
					["isLive", isLive],
					["language", currentLanguage || "en"],
					["sportPartner", window.DIGITAIN_TOKEN],
					["oddsFormat", window.DIGITAIN_ODD_FORMAT],
					["userId", sessionId && sessionId !== "" ? sessionId : ""],
					["parent", [window.DIGITAIN_PARENT_DOMAIN]],
				];
				if (isBetto) {
					sp.push([
						"initialRoute",
						{ path: isLive ? "/live" : "/pre-match", qs: {}, hashData: "" },
					]);
				}
				window.SportFrame.frame(sp);
			}

			if (isBetto) {
				await waitForSportsbookMount(container);
			}
		} finally {
			if (isBetto) {
				setIsBootstrapping(false);
			}
		}
	}, [currentLanguage, isBetto, isLatinoUI, sessionId]);

	useEffect(() => {
		window.addEventListener("resize", renderSB);
		renderSB().then(() => {
			const digitain = document.querySelector("sport-mobile");
			if (digitain) {
				const shadowRoot = digitain.shadowRoot;
				const sportMobile = shadowRoot.querySelector("#sport-mobile");

				const observer = new MutationObserver((mutationsList, observer) => {
					for (const mutation of mutationsList) {
						if (mutation.type === "childList") {
							const shadowElement = sportMobile.querySelector(".dm__betslip--single");
							if (shadowElement) {
								shadowElement.style.bottom = "70px";
								observer.disconnect();
							}
						}
					}
				});

				observer.observe(sportMobile, { childList: true, subtree: true });

				const shadowElement = sportMobile.querySelector(".dm__betslip--single");
				if (shadowElement) {
					shadowElement.style.bottom = "70px";
					observer.disconnect();
				}
			}
		});
		return () => {
			if (window.SportFrame?.elem) {
				window.SportFrame.destroy();
			}
			window.removeEventListener("resize", renderSB);
		};
	}, [renderSB]);

	return (
		<SportsbookRoot>
			<SportsbookFrame
				id="sport_div_iframe"
				ref={containerRef}
				aria-hidden={isBetto && isBootstrapping}
			/>
			{isBetto && isBootstrapping ? (
				<SportsbookLoadingOverlay data-testid="sportsbook-loading-stage">
					<BettoGameStage>
						<BettoGameStageSkeleton
							role="status"
							aria-busy="true"
							aria-label="Loading"
							data-testid="sportsbook-loading-skeleton">
							<BettoLoadingLogo compact />
						</BettoGameStageSkeleton>
					</BettoGameStage>
				</SportsbookLoadingOverlay>
			) : null}
		</SportsbookRoot>
	);
}
